import { Injectable } from '@angular/core';

import {
  CopyPreviousDprFilters,
  DprFreeTextFieldsSectionFilter,
  DprTableOfColumnsSectionFilter,
} from '@dpr-details/models';
import { CopyDprDefaultOptions } from '@dpr-details/enums';
import {
  DprConsumable,
  DprDetails,
  DprFreeTextField,
  DprHse,
  DprPersonnel,
  DprProgress,
  DprProgressWithoutTarget,
  DprSummary,
  DprTableOfColumns,
  DprWeather,
} from '@shared/models/dpr';
import { SubprojectConfiguration } from '@shared/models/project-setup';
import { CopyDprOptionGridData } from '@dpr-details/models/grid';
import { SelectedSubproject } from '@shared/models';

@Injectable()
export class CopyFromPreviousDprService {
  getCopyFromPreviousDprOptions(
    selectedSubproject: SelectedSubproject,
    dprDetails: DprDetails
  ): CopyDprOptionGridData[] {
    const options: CopyDprOptionGridData[] = [
      {
        id: CopyDprDefaultOptions.Activities,
        section:
          selectedSubproject?.data?.subprojectConfiguration.customNames
            .activitiesTabName ??
          $localize`:@@dprDetails.copyFromPreviousDprService.lblActivities:Activities`,
        copy: !dprDetails.dprActivities.length,
        allowIncludeAttachments: true,
        includeAttachments: false,
        allowIncludeCustomData: true,
        includeCustomData: false,
        disable:
          !!dprDetails.dprActivities.length ||
          !!dprDetails.dprParallelActivities.length,
      },
      {
        id: CopyDprDefaultOptions.Personnel,
        section:
          selectedSubproject?.data?.subprojectConfiguration.customNames
            .personnelTabName ??
          $localize`:@@dprDetails.copyFromPreviousDprService.lblPersonnel:Personnel`,
        copy: this.isDprPersonnelEmpty(dprDetails.dprPersonnel),
        allowIncludeAttachments: true,
        includeAttachments: false,
        allowIncludeCustomData: false,
        includeCustomData: false,
        disable: !this.isDprPersonnelEmpty(dprDetails.dprPersonnel),
      },
      selectedSubproject?.data?.subprojectConfiguration.enableStatusTrackerTab
        ? {
            id: CopyDprDefaultOptions.StatusTracker,
            section:
              selectedSubproject?.data?.subprojectConfiguration.customNames
                .statusTrackerTabName ??
              $localize`:@@dprDetails.copyFromPreviousDprService.lblStatusTracker:Status Tracker`,
            copy: !dprDetails.dprStatusTracker?.dprStatusTrackerDetails.length,
            allowIncludeAttachments: false,
            includeAttachments: false,
            allowIncludeCustomData: false,
            includeCustomData: false,
            disable:
              !!dprDetails.dprStatusTracker?.dprStatusTrackerDetails.length,
          }
        : null,
      {
        id: CopyDprDefaultOptions.Weather,
        section:
          selectedSubproject?.data?.subprojectConfiguration.customNames
            .weatherTabName ??
          $localize`:@@dprDetails.copyFromPreviousDprService.lblWeather:Weather`,
        copy: this.isDprWeatherEmpty(dprDetails.dprWeather),
        allowIncludeAttachments: true,
        includeAttachments: false,
        allowIncludeCustomData: false,
        includeCustomData: false,
        disable: !this.isDprWeatherEmpty(dprDetails.dprWeather),
      },
      {
        id: CopyDprDefaultOptions.Consumables,
        section:
          selectedSubproject?.data?.subprojectConfiguration.customNames
            .consumablesTabName ??
          $localize`:@@dprDetails.copyFromPreviousDprService.lblConsumables:Consumables`,
        copy: this.isDprConsumableEmpty(dprDetails.dprConsumable),
        allowIncludeAttachments: true,
        includeAttachments: false,
        allowIncludeCustomData: false,
        includeCustomData: false,
        disable: !this.isDprConsumableEmpty(dprDetails.dprConsumable),
      },
      {
        id: CopyDprDefaultOptions.HSE,
        section:
          selectedSubproject?.data?.subprojectConfiguration.customNames
            .hseTabName ??
          $localize`:@@dprDetails.copyFromPreviousDprService.lblHSE:HSE`,
        copy: this.isDprHseEmpty(dprDetails.dprHSE),
        allowIncludeAttachments: true,
        includeAttachments: false,
        allowIncludeCustomData: false,
        includeCustomData: false,
        disable: !this.isDprHseEmpty(dprDetails.dprHSE),
      },
      {
        id: CopyDprDefaultOptions.Summary,
        section:
          selectedSubproject?.data?.subprojectConfiguration.customNames
            .summaryTabName ??
          $localize`:@@dprDetails.copyFromPreviousDprService.lblSummary:Summary`,
        copy: this.isDprSummaryEmpty(dprDetails.dprSummary),
        allowIncludeAttachments: true,
        includeAttachments: false,
        allowIncludeCustomData: false,
        includeCustomData: false,
        disable: !this.isDprSummaryEmpty(dprDetails.dprSummary),
      },
      selectedSubproject?.data?.subprojectConfiguration.enableForecast
        ? {
            id: CopyDprDefaultOptions.Forecast,
            section:
              selectedSubproject?.data?.subprojectConfiguration.customNames
                .forecastTabName ??
              $localize`:@@dprDetails.copyFromPreviousDprService.lblForecast:Forecast`,
            copy: !dprDetails.dprForecastActivities.length,
            allowIncludeAttachments: false,
            includeAttachments: false,
            allowIncludeCustomData: false,
            includeCustomData: false,
            disable: !!dprDetails.dprForecastActivities.length,
          }
        : null,
      selectedSubproject?.data?.subprojectConfiguration.enableAppendix
        ? {
            id: CopyDprDefaultOptions.Appendix,
            section:
              selectedSubproject?.data?.subprojectConfiguration.customNames
                .appendixTabName ??
              $localize`:@@dprDetails.copyFromPreviousDprService.lblAppendix:Appendix`,
            copy: !dprDetails.dprAppendixAttachments.length,
            allowIncludeAttachments: false,
            includeAttachments: false,
            allowIncludeCustomData: false,
            includeCustomData: false,
            disable: !!dprDetails.dprAppendixAttachments.length,
          }
        : null,
      ...this.addFreeTextFieldsToOptions(dprDetails, selectedSubproject),
      selectedSubproject?.data?.subprojectConfiguration.enableProgressTab
        ? {
            id: CopyDprDefaultOptions.ProgressCountWithTarget,
            section:
              selectedSubproject?.data?.subprojectConfiguration.customNames
                .progressTabName ??
              $localize`:@@dprDetails.copyFromPreviousDprService.lblProgressCountWithTarget:Progress Count With Target`,
            copy: this.isDprProgressEmpty(dprDetails.dprProgress),
            allowIncludeAttachments: true,
            includeAttachments: false,
            allowIncludeCustomData: false,
            includeCustomData: false,
            disable: !this.isDprProgressEmpty(dprDetails.dprProgress),
          }
        : null,
      selectedSubproject?.data?.subprojectConfiguration
        .enableProgressWithoutTargetTab
        ? {
            id: CopyDprDefaultOptions.ProgressCountWithoutTarget,
            section:
              selectedSubproject?.data?.subprojectConfiguration.customNames
                .progressWithoutTargetTabName ??
              $localize`:@@dprDetails.copyFromPreviousDprService.lblProgressCountWithoutTarget:Progress Count Without Target`,
            copy: this.isDprProgressWithoutTargetEmpty(
              dprDetails.dprProgressWithoutTarget
            ),
            allowIncludeAttachments: true,
            includeAttachments: false,
            allowIncludeCustomData: false,
            includeCustomData: false,
            disable: !this.isDprProgressWithoutTargetEmpty(
              dprDetails.dprProgressWithoutTarget
            ),
          }
        : null,
      ...this.addTableOfColumnsToOptions(dprDetails, selectedSubproject),
      selectedSubproject?.data?.subprojectConfiguration.enableHiddenTab
        ? {
            id: CopyDprDefaultOptions.HiddenFreeTextFields,
            section:
              selectedSubproject?.data?.subprojectConfiguration.customNames
                .hiddenTabName ??
              $localize`:@@dprDetails.copyFromPreviousDprService.lblHiddenFreeTextFields:Hidden free text fields`,
            copy: !dprDetails.dprHidden.some(
              (x) =>
                !String.isNullOrWhiteSpace(x.comment) ||
                x.dprHiddenAttachments.length
            ),
            allowIncludeAttachments: true,
            includeAttachments: false,
            allowIncludeCustomData: false,
            includeCustomData: false,
            disable: dprDetails.dprHidden.some(
              (x) =>
                !String.isNullOrWhiteSpace(x.comment) ||
                x.dprHiddenAttachments.length
            ),
          }
        : null,
    ];

    return options.filter((x) => x);
  }

  getCanCopyDpr(
    dprDetails: DprDetails,
    subprojectConfiguration: SubprojectConfiguration
  ): boolean {
    return (
      !dprDetails.lockedDate &&
      ((!dprDetails.dprActivities.length &&
        !dprDetails.dprParallelActivities.length) ||
        this.isDprPersonnelEmpty(dprDetails.dprPersonnel) ||
        (subprojectConfiguration.enableStatusTrackerTab &&
          !dprDetails.dprStatusTracker?.dprStatusTrackerDetails.length) ||
        this.isDprWeatherEmpty(dprDetails.dprWeather) ||
        this.isDprHseEmpty(dprDetails.dprHSE) ||
        this.isDprConsumableEmpty(dprDetails.dprConsumable) ||
        this.isDprSummaryEmpty(dprDetails.dprSummary) ||
        (subprojectConfiguration.enableForecast &&
          !dprDetails.dprForecastActivities.length) ||
        (subprojectConfiguration.enableAppendix &&
          !dprDetails.dprAppendixAttachments.length) ||
        (subprojectConfiguration.enableProgressTab &&
          this.isDprProgressEmpty(dprDetails.dprProgress)) ||
        (subprojectConfiguration.enableProgressWithoutTargetTab &&
          this.isDprProgressWithoutTargetEmpty(
            dprDetails.dprProgressWithoutTarget
          )) ||
        (subprojectConfiguration.enableHiddenTab &&
          !dprDetails.dprHidden.some(
            (x) =>
              !String.isNullOrWhiteSpace(x.comment) ||
              x.dprHiddenAttachments.length
          )))
    );
  }

  convertCopyPreviousDprOptionsToFilters(
    subprojectId: number,
    dprDetailId: number | string,
    options: CopyDprOptionGridData[]
  ): CopyPreviousDprFilters {
    return {
      subprojectId,
      dprDetailId,
      ...options.reduce(
        (acc, curr) => Object.assign(acc, this.mapData(curr)),
        {}
      ),
      includeDprTableOfColumnsSectionFilters:
        this.mapDprTableOfColumnsData(options),
      includeDprFreeTextFieldsSectionFilters:
        this.mapDprFreeTextFieldsData(options),
    };
  }

  //#region Private methods

  private mapData(option: CopyDprOptionGridData): any {
    switch (option.id) {
      case CopyDprDefaultOptions.Activities:
        return {
          includeDprActivities: option.copy,
          includeDprActivitiesAttachments: option.includeAttachments,
          includeDprActivitiesCustomData: option.includeCustomData,
        };
      case CopyDprDefaultOptions.Personnel:
        return {
          includeDprPersonnel: option.copy,
          includeDprPersonnelAttachments: option.includeAttachments,
        };
      case CopyDprDefaultOptions.StatusTracker:
        return {
          includeDprStatusTracker: option.copy,
        };
      case CopyDprDefaultOptions.Weather:
        return {
          includeDprWeather: option.copy,
          includeDprWeatherAttachments: option.includeAttachments,
        };
      case CopyDprDefaultOptions.HSE:
        return {
          includeDprHse: option.copy,
          includeDprHseAttachments: option.includeAttachments,
        };
      case CopyDprDefaultOptions.Consumables:
        return {
          includeDprConsumables: option.copy,
          includeDprConsumablesAttachments: option.includeAttachments,
        };
      case CopyDprDefaultOptions.Summary:
        return {
          includeDprSummary: option.copy,
          includeDprSummaryAttachments: option.includeAttachments,
        };
      case CopyDprDefaultOptions.Forecast:
        return {
          includeDprForecast: option.copy,
        };
      case CopyDprDefaultOptions.Appendix:
        return {
          includeDprAppendix: option.copy,
        };
      case CopyDprDefaultOptions.ProgressCountWithTarget:
        return {
          includeDprProgress: option.copy,
          includeDprProgressAttachments: option.includeAttachments,
        };
      case CopyDprDefaultOptions.ProgressCountWithoutTarget:
        return {
          includeDprProgressWithoutTarget: option.copy,
          includeDprProgressWithoutTargetAttachments: option.includeAttachments,
        };
      case CopyDprDefaultOptions.HiddenFreeTextFields:
        return {
          includeDprHidden: option.copy,
          includeDprHiddenAttachments: option.includeAttachments,
        };
      default:
        return {};
    }
  }

  private mapDprTableOfColumnsData(options: CopyDprOptionGridData[]): any {
    const dprTableOfColumnsOptions: DprTableOfColumnsSectionFilter[] = [];
    options
      .filter((option) => option.isDprTableOfColumns)
      .map((option) => {
        dprTableOfColumnsOptions.push({
          section: option.section,
          includeDprTableOfColumn: option.copy,
          includeDprTableOfColumnAttachments: option.includeAttachments,
          tableOfColumnId: option.id.toString().replace('toc', ''),
        });
      });
    return dprTableOfColumnsOptions;
  }

  private mapDprFreeTextFieldsData(options: CopyDprOptionGridData[]): any {
    const dprFreeTextFieldOptions: DprFreeTextFieldsSectionFilter[] = [];
    options
      .filter((option) => option.isDprFreeTextField)
      .map((option) => {
        dprFreeTextFieldOptions.push({
          section: option.section,
          includeDprFreeTextField: option.copy,
          freeTextFieldId: option.id.toString().replace('ftf', ''),
        });
      });

    return dprFreeTextFieldOptions;
  }

  private isDprConsumableEmpty(dprConsumable: DprConsumable): boolean {
    return !(
      dprConsumable.comment ||
      dprConsumable.dprConsumableAttachments.length ||
      // we have to check after id because some tabs have default columns
      // default columns don't have an id unless edited
      dprConsumable.dprConsumableDetails.some((x) => x.id)
    );
  }

  private isDprHseEmpty(dprHSE: DprHse): boolean {
    return !(
      dprHSE.comment ||
      dprHSE.dprhseAttachments.length ||
      dprHSE.dprhseDetails.some((x) => x.id)
    );
  }

  private isDprPersonnelEmpty(dprPersonnel: DprPersonnel): boolean {
    return !(
      dprPersonnel.comment ||
      dprPersonnel.dprPersonnelAttachments.length ||
      dprPersonnel.dprPersonnelDetails.some((x) => x.id)
    );
  }

  private isDprSummaryEmpty(dprSummary: DprSummary): boolean {
    return !(
      dprSummary.currentComment ||
      dprSummary.nextComment ||
      dprSummary.externalComment ||
      dprSummary.dprSummaryAttachments.length
    );
  }

  private isDprWeatherEmpty(dprWeather: DprWeather): boolean {
    return !(
      dprWeather.comment ||
      dprWeather.dprWeatherAttachments.length ||
      dprWeather.dprWeatherDetails.length
    );
  }

  private isDprProgressEmpty(dprProgress: DprProgress): boolean {
    return !(
      dprProgress.comment ||
      dprProgress.dprProgressAttachments.length ||
      dprProgress.dprProgressDetails.some((x) => x.id)
    );
  }

  private isDprProgressWithoutTargetEmpty(
    dprProgressWithoutTarget: DprProgressWithoutTarget
  ): boolean {
    return !(
      dprProgressWithoutTarget.comment ||
      dprProgressWithoutTarget.dprProgressWithoutTargetAttachments.length ||
      dprProgressWithoutTarget.dprProgressWithoutTargetDetails.some((x) => x.id)
    );
  }

  private isDprTableOfColumnsEmpty(
    dprTableOfColumns: DprTableOfColumns
  ): boolean {
    return !(
      dprTableOfColumns?.comment ||
      dprTableOfColumns?.dprTableOfColumnsAttachment.length ||
      dprTableOfColumns?.dprTableOfColumnsDetails.length
    );
  }

  private isDprFreeTextFieldEmpty(dprFreeTextField: DprFreeTextField): boolean {
    return !dprFreeTextField?.dprFreeTextFieldDetails.some(
      (detail) => !String.isNullOrWhiteSpace(detail.comment)
    );
  }

  private addTableOfColumnsToOptions(
    dprDetails: DprDetails,
    selectedSubproject: SelectedSubproject
  ): CopyDprOptionGridData[] {
    const columns = [];

    selectedSubproject.data?.tableOfColumns.forEach((tableOfColumn) => {
      const dprTableOfColumn = dprDetails.dprTableOfColumns.find(
        (x) => x.tableOfColumnId === tableOfColumn.id
      );

      const newOption: CopyDprOptionGridData = {
        id: `toc${tableOfColumn.id}`,
        section: tableOfColumn.name,
        copy: this.isDprTableOfColumnsEmpty(dprTableOfColumn),
        allowIncludeAttachments: true,
        includeAttachments: false,
        allowIncludeCustomData: false,
        includeCustomData: false,
        disable: !this.isDprTableOfColumnsEmpty(dprTableOfColumn),
        isDprTableOfColumns: true,
      };

      columns.push(newOption);
    });

    return columns;
  }

  private addFreeTextFieldsToOptions(
    dprDetails: DprDetails,
    selectedSubproject: SelectedSubproject
  ): CopyDprOptionGridData[] {
    const columns = [];

    selectedSubproject.data?.freeTextFields?.forEach((freeTextField) => {
      const dprFreeTextField = dprDetails.dprFreeTextFields.find(
        (x) => x.freeTextFieldId === freeTextField.id
      );

      const newOption: CopyDprOptionGridData = {
        id: `ftf${freeTextField.id}`,
        section: freeTextField.name,
        copy: this.isDprFreeTextFieldEmpty(dprFreeTextField),
        allowIncludeAttachments: false,
        includeAttachments: false,
        allowIncludeCustomData: false,
        includeCustomData: false,
        disable: !this.isDprFreeTextFieldEmpty(dprFreeTextField),
        isDprFreeTextField: true,
      };

      columns.push(newOption);
    });

    return columns;
  }

  //#endregion
}
